<template>
  <section
    class="ct"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <header class="ct__header">
      <button
        type="button"
        class="ct__back"
        :aria-label="$t('chat.back')"
        @click="$emit('back')"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="22"
        />
      </button>
      <chat-avatar
        :name="peerName"
        :photo="peerPhoto"
        :size="42"
        :status="presence"
      />
      <div class="ct__who">
        <strong>{{ peerName }}</strong>
        <small :class="`ct__state ct__state--${presence}`">{{ presenceLabel }}</small>
      </div>
    </header>

    <div class="ct__body">
      <div
        ref="scroller"
        class="ct__scroll"
        @scroll.passive="onScroll"
      >
        <div
          v-if="!thread || !thread.loaded"
          class="ct__loading"
        >
          <b-spinner small /> {{ $t('chat.loading') }}
        </div>

        <template v-else>
          <div
            v-if="thread.loadingOlder"
            class="ct__older"
          >
            <b-spinner small />
          </div>
          <p
            v-else-if="!thread.hasMore && thread.items.length"
            class="ct__start"
          >{{ $t('chat.conversationStart') }}</p>

          <div
            v-if="!thread.items.length"
            class="ct__empty"
          >
            <span class="ct__empty-icon"><feather-icon
              icon="MessageCircleIcon"
              size="30"
            /></span>
            <strong>{{ $t('chat.newConversationTitle', { name: peerName }) }}</strong>
            <small>{{ $t('chat.newConversationHint') }}</small>
          </div>

          <template v-for="entry in entries">
            <div
              v-if="entry.day"
              :key="entry.key"
              class="ct__day"
            ><span>{{ entry.day }}</span></div>
            <div
              v-else
              :key="entry.key"
              class="ct__row"
              :class="{ 'ct__row--select': selecting, 'ct__row--on': isSelected(entry.message) }"
              @click.capture="onRowClick($event, entry.message)"
            >
              <span
                v-if="selecting"
                class="ct__check"
                :class="{ 'ct__check--on': isSelected(entry.message), 'ct__check--off': !entry.message.id }"
              ><feather-icon
                v-if="isSelected(entry.message)"
                icon="CheckIcon"
                size="14"
              /></span>
              <chat-message-bubble
                :message="entry.message"
                :mine="entry.mine"
                :tail="entry.tail"
                :peer-read-id="thread.peerReadId"
                :peer-delivered-id="thread.peerDeliveredId"
                :peer-name="peerName"
                :selecting="selecting"
                @retry="m => $store.dispatch('chat/retry', { conversationId: conversation.id, clientUuid: m.client_uuid })"
                @discard="m => $store.dispatch('chat/discardFailed', { conversationId: conversation.id, clientUuid: m.client_uuid })"
                @open-image="openImage"
                @reply="startReply"
                @edit="startEdit"
                @copy="copyText"
                @forward="startForward"
                @info="m => info = { show: true, message: m }"
                @jump="jumpTo"
              />
            </div>
          </template>
        </template>

      </div>
      <transition name="ct-fab">
        <button
          v-if="!atBottom && thread && thread.loaded"
          type="button"
          class="ct__fab"
          :aria-label="$t('chat.scrollToEnd')"
          @click="scrollToBottom(true)"
        >
          <feather-icon
            icon="ChevronDownIcon"
            size="20"
          />
          <span
            v-if="newCount"
            class="ct__fab-count"
          >{{ newCount }}</span>
        </button>
      </transition>
    </div>

    <transition name="ct-drop">
      <div
        v-if="dragging"
        class="ct__drop"
      >
        <feather-icon
          icon="UploadCloudIcon"
          size="44"
        />
        <strong>{{ $t('chat.dropHere') }}</strong>
      </div>
    </transition>

    <chat-composer
      v-show="!selecting"
      ref="composer"
      :reply="replyTo"
      :editing="editing"
      :reply-name="replyName"
      @send-text="body => send({ body })"
      @send-file="({ file, body }) => send({ file, body })"
      @send-voice="({ file, duration }) => send({ file, duration, voice: true })"
      @save-edit="saveEdit"
      @cancel-context="clearContext"
    />

    <!-- Modo de seleção: no lugar do campo de texto, a barra com a contagem e o encaminhar -->
    <div
      v-if="selecting"
      class="ct__selbar"
    >
      <button
        type="button"
        class="ct__selbar-btn"
        :aria-label="$t('chat.cancel')"
        @click="cancelSelect"
      >
        <feather-icon
          icon="XIcon"
          size="22"
        />
      </button>
      <strong>{{ $tc('chat.selected', selectedIds.length, { count: selectedIds.length }) }}</strong>
      <button
        type="button"
        class="ct__selbar-btn ct__selbar-btn--go"
        :disabled="!selectedIds.length"
        :title="$t('chat.menu.forward')"
        :aria-label="$t('chat.menu.forward')"
        @click="openForward"
      >
        <feather-icon
          icon="CornerUpRightIcon"
          size="22"
        />
      </button>
    </div>

    <chat-message-info
      :visible="info.show"
      :message="info.message"
      @close="info.show = false"
    />

    <chat-forward-modal
      :visible="forwarding.show"
      :recent="forwardRecent"
      :contacts="forwardContacts"
      :max="FORWARD_MAX"
      :busy="forwarding.busy"
      @close="forwarding.show = false"
      @confirm="confirmForward"
    />

    <b-modal
      v-model="lightbox.show"
      centered
      size="xl"
      hide-footer
      hide-header
      body-class="ct__lightbox"
    >
      <img
        v-if="lightbox.message"
        :src="lightbox.message.attachment.url"
        :alt="lightbox.message.attachment.name"
      >
      <a
        v-if="lightbox.message"
        class="ct__lightbox-download"
        :href="lightbox.message.attachment.url"
        :download="lightbox.message.attachment.name"
      ><feather-icon
        icon="DownloadIcon"
        size="16"
      /> {{ $t('chat.download') }}</a>
    </b-modal>
  </section>
</template>

<script>
import { dayLabel, daysAgo, resolvePhoto } from '@/utils/chat'
import ChatAvatar from './ChatAvatar.vue'
import ChatComposer from './ChatComposer.vue'
import ChatMessageBubble from './ChatMessageBubble.vue'
import ChatForwardModal from './ChatForwardModal.vue'
import ChatMessageInfo from './ChatMessageInfo.vue'

const BOTTOM_THRESHOLD = 80
const LOAD_OLDER_THRESHOLD = 140
const GROUP_WINDOW_MS = 5 * 60 * 1000
const FORWARD_MAX = 5

export default {
  name: 'ChatThread',

  components: {
    ChatAvatar, ChatComposer, ChatMessageBubble, ChatForwardModal, ChatMessageInfo,
  },

  props: {
    conversation: { type: Object, required: true },
  },

  data() {
    return {
      atBottom: true,
      newCount: 0,
      dragging: false,
      dragDepth: 0,
      lightbox: { show: false, message: null },
      replyTo: null,
      editing: null,
      selecting: false,
      selectedIds: [],
      forwarding: { show: false, busy: false },
      info: { show: false, message: null },
      FORWARD_MAX,
    }
  },

  computed: {
    thread() {
      return this.$store.state.chat.threads[this.conversation.id] || null
    },
    me() {
      return this.$store.state.user.id
    },
    peerPhoto() {
      return this.conversation.peer ? this.conversation.peer.photo_url : null
    },
    peerName() {
      return this.conversation.peer ? this.conversation.peer.name : ''
    },
    replyName() {
      return this.replyTo && this.replyTo.sender_id === this.me ? this.$t('chat.you') : this.peerName
    },
    // Destinos do encaminhar: conversas que já existem e, depois, quem está online e ainda não tem conversa.
    forwardRecent() {
      return this.$store.state.chat.conversations.filter(c => c.peer).map(c => ({
        key: `c${c.id}`, name: c.peer.name, photo: resolvePhoto(this.$store.state.url, c.peer.photo_url), conversationId: c.id,
      }))
    },
    forwardContacts() {
      const talking = new Set(this.$store.state.chat.conversations.filter(c => c.peer).map(c => c.peer.user_id))

      return this.$store.state.chat.contacts.filter(c => !talking.has(c.user_id)).map(c => ({
        key: `u${c.user_id}`, name: c.name, photo: resolvePhoto(this.$store.state.url, c.photo_url), userId: c.user_id,
      }))
    },
    // Estado de quem está do outro lado: o do contato (se estiver no escopo) ou apenas online/offline.
    presence() {
      return this.$store.getters['chat/presenceOf'](this.conversation.peer)
    },
    presenceLabel() {
      return this.$t(`chat.status.${this.presence}`)
    },
    edges() {
      const items = this.thread ? this.thread.items : []

      return `${items.length}|${items.length ? items[0].client_uuid : ''}|${items.length ? items[items.length - 1].client_uuid : ''}`
    },
    // Lista plana (separador de dia + mensagem) para o template iterar sem aninhar.
    entries() {
      if (!this.thread) return []
      const labels = { today: this.$t('chat.today'), yesterday: this.$t('chat.yesterday') }
      const out = []
      let lastDay = null
      let previous = null

      this.thread.items.forEach(message => {
        const day = daysAgo(message.created_at)
        if (day !== lastDay) {
          out.push({ key: `day-${message.created_at.slice(0, 10)}`, day: dayLabel(message.created_at, this.$i18n.locale, labels) })
          lastDay = day
          previous = null
        }
        const sameGroup = previous
          && previous.sender_id === message.sender_id
          && new Date(message.created_at) - new Date(previous.created_at) < GROUP_WINDOW_MS
        out.push({
          key: message.client_uuid, message, mine: message.sender_id === this.me, tail: !sameGroup,
        })
        previous = message
      })

      return out
    },
  },

  watch: {
    // Entrar/sair da seleção muda a altura do que está embaixo (barra no lugar do campo) e a largura das bolhas:
    // quem estava no fim da conversa continua no fim, em vez de ver a lista "subir".
    selecting() {
      const stick = this.atBottom
      this.afterRender(() => { if (stick) this.scrollToBottom(false) })
    },
    'conversation.id': {
      immediate: true,
      handler() {
        this.prevEdges = null
        this.atBottom = true
        this.newCount = 0
        this.afterRender(() => {
          this.scrollToBottom(false)
          this.focusComposer()
        })
      },
    },
    // Roda antes do DOM atualizar: dá para guardar a posição de rolagem de antes.
    edges() {
      const el = this.$refs.scroller
      const items = this.thread ? this.thread.items : []
      const prev = this.prevEdges
      const next = { length: items.length, first: items.length ? items[0].client_uuid : null, last: items.length ? items[items.length - 1] : null }
      this.prevEdges = { length: next.length, first: next.first, last: next.last ? next.last.client_uuid : null }
      if (!el || !prev || !prev.length) {
        if (el) this.afterRender(() => this.scrollToBottom(false))
        return
      }

      const before = { height: el.scrollHeight, top: el.scrollTop }
      const prepended = next.length > prev.length && next.first !== prev.first
      const appended = next.last && next.last.client_uuid !== prev.last

      this.afterRender(() => {
        if (prepended) {
          // Página anterior entrou no topo: o que o usuário lia fica no mesmo lugar.
          el.scrollTop = before.top + (el.scrollHeight - before.height)
        } else if (appended) {
          if (this.atBottom || next.last.sender_id === this.me) this.scrollToBottom(true)
          else this.newCount += 1
        }
        this.markReadIfSeen()
      })
    },
  },

  created() {
    // Pontas da lista na última renderização (não reativo, só para comparar).
    this.prevEdges = null
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    document.addEventListener('visibilitychange', this.markReadIfSeen)
    window.addEventListener('focus', this.markReadIfSeen)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('visibilitychange', this.markReadIfSeen)
    window.removeEventListener('focus', this.markReadIfSeen)
  },

  methods: {
    onKeydown(event) {
      if (event.key === 'Escape' && this.selecting && !this.forwarding.show) this.cancelSelect()
    },

    afterRender(fn) {
      this.$nextTick(fn)
    },

    focusComposer() {
      if (this.$refs.composer && window.matchMedia('(min-width: 768px)').matches) this.$refs.composer.focus()
    },

    send(payload) {
      // A citação vai só na primeira mensagem enviada depois de escolher "Responder".
      const { replyTo } = this
      this.replyTo = null
      this.$store.dispatch('chat/send', {
        conversationId: this.conversation.id, body: null, file: null, voice: false, duration: null, replyTo, ...payload,
      })
    },

    clearContext() {
      this.replyTo = null
      this.editing = null
    },

    startReply(message) {
      this.editing = null
      this.replyTo = message
    },

    startEdit(message) {
      this.replyTo = null
      this.editing = message
    },

    async saveEdit(body) {
      const message = this.editing
      if (!message) return
      if (body === message.body) {
        this.clearContext()
        return
      }
      try {
        await this.$store.dispatch('chat/edit', { messageId: message.id, body })
        this.clearContext()
      } catch (e) {
        const errors = e.response && e.response.data && e.response.data.errors
        this.$message(null, errors ? Object.values(errors)[0][0] : this.$t('chat.errors.editFailed'), 'error')
      }
    },

    async copyText(message) {
      try {
        await navigator.clipboard.writeText(message.body)
      } catch (e) {
        // Contexto sem permissão da área de transferência: seleciona num campo temporário.
        const field = document.createElement('textarea')
        field.value = message.body
        document.body.appendChild(field)
        field.select()
        document.execCommand('copy')
        document.body.removeChild(field)
      }
      this.$message(null, this.$t('chat.copied'), 'success')
    },

    startSelect(message) {
      this.clearContext()
      this.selecting = true
      this.selectedIds = [message.id]
    },

    // "Encaminhar" no menu de uma mensagem já entra no modo de seleção com ela marcada, como no WhatsApp.
    startForward(message) {
      this.startSelect(message)
    },

    cancelSelect() {
      this.selecting = false
      this.selectedIds = []
    },

    isSelected(message) {
      return !!message.id && this.selectedIds.includes(message.id)
    },

    onRowClick(event, message) {
      if (!this.selecting) return
      event.stopPropagation()
      event.preventDefault()
      if (!message.id) return
      this.selectedIds = this.isSelected(message) ? this.selectedIds.filter(id => id !== message.id) : this.selectedIds.concat(message.id)
    },

    openForward() {
      if (this.selectedIds.length) this.forwarding = { show: true, busy: false }
    },

    async confirmForward(targets) {
      this.forwarding.busy = true
      try {
        // Quem ainda não tem conversa ganha uma agora (só funciona se estiver online, como ao abrir pelo menu).
        // Uma por vez: duas aberturas simultâneas para a mesma pessoa não devem disputar a criação da conversa.
        const ids = await targets.reduce(async (previous, target) => {
          const list = await previous
          list.push(target.conversationId || await this.$store.dispatch('chat/ensureConversation', target.userId))

          return list
        }, Promise.resolve([]))
        // Na ordem em que foram escritas, não na ordem em que foram marcadas.
        const ordered = this.thread.items.filter(m => this.selectedIds.includes(m.id)).map(m => m.id)
        await this.$store.dispatch('chat/forward', { messageIds: ordered, conversationIds: ids })
        this.forwarding.show = false
        this.cancelSelect()
        this.$message(null, this.$t('chat.forwardDone'), 'success')
      } catch (e) {
        const errors = e.response && e.response.data && e.response.data.errors
        this.$message(null, errors ? Object.values(errors)[0][0] : this.$t('chat.errors.forwardFailed'), 'error')
      } finally {
        this.forwarding.busy = false
      }
    },

    // Rola até a mensagem citada (se já estiver carregada) e a destaca por um instante.
    jumpTo(messageId) {
      const el = this.$refs.scroller && this.$refs.scroller.querySelector(`[data-mid="${messageId}"]`)
      if (!el) return
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      el.classList.remove('cb--flash')
      // Reflow para a animação reiniciar se o usuário clicar de novo na mesma citação.
      void el.offsetWidth // eslint-disable-line no-void
      el.classList.add('cb--flash')
      setTimeout(() => el.classList.remove('cb--flash'), 1500)
    },

    onScroll() {
      const el = this.$refs.scroller
      this.atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_THRESHOLD
      if (this.atBottom) {
        this.newCount = 0
        this.markReadIfSeen()
      }
      if (el.scrollTop < LOAD_OLDER_THRESHOLD && this.thread && this.thread.hasMore) {
        this.$store.dispatch('chat/loadOlder', this.conversation.id)
      }
    },

    scrollToBottom(smooth) {
      const el = this.$refs.scroller
      if (!el) return
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
      this.atBottom = true
      this.newCount = 0
    },

    // Só conta como lida se a aba está visível e o fim da conversa está na tela.
    markReadIfSeen() {
      if (document.visibilityState !== 'visible' || !this.atBottom) return
      this.$store.dispatch('chat/markRead', this.conversation.id)
    },

    openImage(message) {
      this.lightbox = { show: true, message }
    },

    onDragEnter(event) {
      if (!event.dataTransfer || !Array.from(event.dataTransfer.types).includes('Files')) return
      this.dragDepth += 1
      this.dragging = true
    },

    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (!this.dragDepth) this.dragging = false
    },

    onDrop(event) {
      this.dragDepth = 0
      this.dragging = false
      if (event.dataTransfer && event.dataTransfer.files.length) this.$refs.composer.stage(Array.from(event.dataTransfer.files))
    },
  },
}
</script>

<style scoped>
.ct {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  background: var(--chat-wallpaper);
}

.ct__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--chat-border);
  background: var(--chat-header);
}

.ct__back {
  display: none;
  padding: 4px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--chat-muted);
}

.ct__who { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
.ct__who strong { overflow: hidden; color: var(--chat-text); text-overflow: ellipsis; white-space: nowrap; }
.ct__state { color: var(--chat-muted); }
.ct__state--operating { color: #28c76f; }
.ct__state--idle { color: #ff9f43; }
.ct__state--paused { color: #ea5455; }

.ct__body {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.ct__scroll {
  flex: 1;
  padding: 14px clamp(12px, 5%, 64px) 10px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* Linha da mensagem: no modo de seleção ganha a caixa à esquerda e o clique marca em vez de abrir. */
.ct__row { display: flex; align-items: center; gap: 12px; }
.ct__row > .cb { flex: 1; min-width: 0; }

.ct__row--select {
  margin: 0 calc(-1 * clamp(12px, 5%, 64px));
  padding: 0 clamp(12px, 5%, 64px);
  cursor: pointer;
}

.ct__row--select:hover { background: rgba(127, 127, 127, 0.08); }
.ct__row--on, .ct__row--on:hover { background: rgba(115, 103, 240, 0.14); }

.ct__check {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid var(--chat-muted);
  border-radius: 5px;
  color: #fff;
}

.ct__check--on { border-color: #7367f0; background: #7367f0; }
.ct__check--off { opacity: 0.35; }

.ct__selbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-top: 1px solid var(--chat-border);
  background: var(--chat-header);
  color: var(--chat-text);
}

.ct__selbar strong { flex: 1; font-weight: 500; }

.ct__selbar-btn {
  display: inline-flex;
  padding: 6px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--chat-muted);
}

.ct__selbar-btn:hover { background: rgba(127, 127, 127, 0.16); }
.ct__selbar-btn--go { color: #7367f0; }
.ct__selbar-btn:disabled { opacity: 0.4; }

.ct__loading,
.ct__older {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px;
  color: var(--chat-muted);
}

.ct__start {
  margin: 0 auto 8px;
  padding: 4px 12px;
  width: fit-content;
  border-radius: 8px;
  background: var(--chat-pill);
  color: var(--chat-muted);
  font-size: 0.75rem;
}

.ct__day {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  margin: 12px 0 6px;
  pointer-events: none;
}

.ct__day span {
  padding: 3px 12px;
  border-radius: 8px;
  background: var(--chat-pill);
  box-shadow: 0 1px 1px rgba(34, 41, 47, 0.1);
  color: var(--chat-muted);
  font-size: 0.74rem;
  font-weight: 600;
  text-transform: capitalize;
}

.ct__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 12vh auto 0;
  max-width: 320px;
  color: var(--chat-muted);
  text-align: center;
}

.ct__empty strong { color: var(--chat-text); }

.ct__empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 6px;
  border-radius: 50%;
  background: rgba(115, 103, 240, 0.14);
  color: #7367f0;
}

.ct__fab {
  position: absolute;
  right: 18px;
  bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: var(--chat-surface);
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.28);
  color: var(--chat-muted);
}

.ct__fab-count {
  position: absolute;
  top: -8px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #7367f0;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}

.ct__drop {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 2px dashed #7367f0;
  background: rgba(115, 103, 240, 0.16);
  color: #7367f0;
  backdrop-filter: blur(2px);
  pointer-events: none;
}

.ct-fab-enter-active, .ct-fab-leave-active,
.ct-drop-enter-active, .ct-drop-leave-active { transition: opacity 0.15s ease; }
.ct-fab-enter, .ct-fab-leave-to,
.ct-drop-enter, .ct-drop-leave-to { opacity: 0; }

@media (max-width: 767px) {
  .ct__back { display: inline-flex; }
  .ct__header { gap: 8px; padding: 8px 10px; }
}
</style>

<style>
.ct__lightbox {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px !important;
  background: #000;
}

.ct__lightbox img { max-width: 100%; max-height: 78vh; border-radius: 4px; }

.ct__lightbox-download {
  position: absolute;
  right: 16px;
  bottom: 16px;
  padding: 6px 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 0.82rem;
}
</style>
